import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ChartConfiguration,
  ChartData,
  ChartOptions,
  ChartType,
  DoughnutControllerChartOptions,
  Plugin
} from 'chart.js';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { assign, each, merge } from 'lodash';
import { ViveProgress } from 'src/app/classes/models';
import { ViveStatuses } from 'src/app/interfaces/vive-statuses';
import { AuthService } from 'src/app/services/auth.service';
import { SharedDataService } from 'src/app/services/shared-data.service';

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrls: ['./charts.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChartsComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private SharedDataService: SharedDataService,
    private AuthService: AuthService
  ) {}

  @Input() chartType!: ChartType;
  @Input() chartData!: ChartData;
  @Input() chartOptions!: ChartOptions | DoughnutControllerChartOptions;
  @Input() chartPlugins?: Plugin<ChartType>[];
  @Input() chartTitle?: string;
  @Input() chartId!: string;

  @Input() displayChartTitle: boolean = false;

  backgroundColors: Array<{ primary: string; secondary: string }> = [
    { primary: '#A078B0', secondary: 'rgb(160, 120, 176)' },
    { primary: '#1A08D2', secondary: 'rgb(26, 8, 210)' },
    { primary: '#2B716F', secondary: 'rgb(43, 113, 111)' },
    { primary: '#4C84B1', secondary: 'rgb(76, 132, 177)' },
    { primary: '#853619', secondary: 'rgb(133, 54, 25)' },
    { primary: '#CBDEE8', secondary: 'rgb(203, 222, 232)' },
    { primary: '#401F89', secondary: 'rgb(64, 31, 137)' },
    { primary: '#251B30', secondary: 'rgb(37, 27, 48)' },
    { primary: '#83ECD8', secondary: 'rgb(131, 236, 216)' },
    { primary: '#C5698F', secondary: 'rgb(197, 105, 143)' },
    { primary: '#D5DBBF', secondary: 'rgb(213, 219, 191)' },
    { primary: '#905C5D', secondary: 'rgb(144, 92, 93)' },
    { primary: '#EFD994', secondary: 'rgb(239, 217, 148)' },
    { primary: '#9E0B29', secondary: 'rgb(158, 11, 41)' },
    { primary: '#08331E', secondary: 'rgb(8, 51, 30)' },
    { primary: '#3A605B', secondary: 'rgb(58, 96, 91)' },
    { primary: '#0C8F77', secondary: 'rgb(12, 143, 119)' },
    { primary: '#6F7C79', secondary: 'rgb(111, 124, 121)' },
    { primary: '#673E2F', secondary: 'rgb(103, 62, 47)' },
    { primary: '#D27980', secondary: 'rgb(210, 121, 128)' }

    // { primary: 'rgb(0, 135, 238)', secondary: '#C1CBD1' },
    // { primary: 'rgb(255, 127, 0)', secondary: '#B2B9E1' },
    // { primary: 'rgb(15, 191, 149)', secondary: '#A6D5FA' },
    // { primary: 'rgb(4, 30, 57)', secondary: '#99E3C0' },
    // { primary: 'rgb(214, 40, 40)', secondary: '#DFF29E' },
    // { primary: 'rgb(154, 205, 50)', secondary: '#DCE2A3' },
    // { primary: 'rgb(80, 92, 101)', secondary: 'rgb(112, 133, 147)' }
  ];

  chart!: Chart;
  chartConfig: ChartConfiguration = {
    type: this.chartType,
    data: {
      labels: [],
      datasets: [
        {
          data: []
        }
      ]
    },
    plugins: [],
    options: {}
  };

  private loadChartData() {
    this.chartConfig.type = this.chartType;
    merge(this.chartConfig.data, this.chartData);
    merge(this.chartConfig.options, this.chartOptions);
    merge(this.chartConfig, { plugins: this.chartPlugins });

    this.chartId = this.chartId.replace(/\s/g, '').concat('-Chart');

    each(this.chartConfig.data.datasets, (dataset) => {
      if (!dataset.backgroundColor) {
        Object.assign(dataset, {
          backgroundColor: (this.chartConfig.data.labels! as string[]).map(
            (value, index) => {
              if (value === 'Approved') {
                return 'rgb(15, 191, 149)';
              }

              if (['Reviewing', 'Registered'].includes(value)) {
                return 'rgb(255, 127, 0)';
              }

              if (['Suspended'].includes(value)) {
                return 'rgb(214, 40, 40)';
              }

              // Modulo operator ensures the index wraps around if it exceeds the length of backgroundColors
              return this.backgroundColors[index % this.backgroundColors.length]
                .primary;
            }
          )
        });
      }
    });
  }

  private loadChart() {
    this.chart = new Chart(this.chartId, this.chartConfig);
    if (this.chart) {
      this.chart.canvas.onclick = this.handleClickEvent.bind(this);
    }
  }

  private handleClickEvent(click: MouseEvent) {
    const points = this.chart.getElementsAtEventForMode(
      click,
      'nearest',
      { intersect: true },
      true
    );

    if (points && points[0]) {
      const { index } = points[0];

      if ((index || index === 0) && this.chartData.labels) {
        const label = this.chartData.labels[index] as string;
        const user = this.AuthService.getUser();
        const { UserAccountType: { name } = {} } = user;

        if (this.isViveStatus(label)) {
          if (name === 'Admin') {
            return this.SharedDataService.sendEvent(
              'viveAdminStatusLabelSelected',
              label
            );
          }

          if (name === 'Client') {
            return this.SharedDataService.sendEvent(
              'viveClientStatusLabelSelected',
              label
            );
          }
        }

        if (this.isViveProgressType(label)) {
          return this.SharedDataService.sendEvent(
            'viveProgressTypeLabelSelected',
            label
          );
        }

        return this.SharedDataService.sendEvent('chartClicked', label);
      }
    }
  }

  isViveStatus(string: any): string is ViveStatuses {
    const viveStatusesArray: ViveStatuses[] = [
      'Unregistered',
      'Pending',
      'Approved',
      'Watch List',
      'Non-Compliant',
      'Suspended',
      'Declined',
      'Inactive',
      'Resolving',
      'Registering',
      'Awaiting Payment',
      'Registered',
      'Reviewing',
      'Renewing',
      'Site Conditional',
      'Compliance Exempt',
      'Business Verified',
      'Non-Verified'
    ];

    return viveStatusesArray.includes(string);
  }

  isViveProgressType(string: any): string is ViveProgress['type'] {
    const viveProgressTypes: ViveProgress['type'][] = [
      'Insurance',
      'Trades & Licenses',
      'Vendor Info',
      'Owner Info',
      'Final Review'
    ];

    return viveProgressTypes.includes(string);
  }

  ngOnInit(): void {
    this.loadChartData();

    this.SharedDataService.listenForEvent().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'loadMoreTrades') {
          this.chart.destroy();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.loadChart();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
